import { AuthScreen } from './base-auth-screen';

export class ProfileAuthScreen extends AuthScreen {
  constructor(screen) {
    super(screen);

    // Since `FormHandler` calls `transformData()` as part of its constructor (which
    // gets run as part of the `AuthScreen` constructor), we need to ensure that
    // `super()` runs before we set the options. Otherwise we'll get an error
    // and the form won't be initialized correctly.
    //
    // Instead we can override the options after the `FormHandler` is initialized.
    this.formHandler.options = {
      ...this.formHandler.options,

      onBeforeSubmit: () => {
        const avatarUpload = this.getAvatarUploadElement();
        const isAvatarValid = !avatarUpload || !avatarUpload?.file || avatarUpload?.status === 'COMPLETE';

        if (!isAvatarValid) {
          this.formHandler.setServerMessage('Please wait for the avatar upload to complete.');
          this.formHandler.shakeSubmitButton();
        }

        return isAvatarValid;
      },

      transformData: (data) => {
        const avatarUpload = this.getAvatarUploadElement();

        if (avatarUpload) {
          data.avatar_s3_source_url = avatarUpload.s3FullUrl;
        }

        return data;
      },

      arrayFields: ['intentions'],
    };

    this.avatarFileInput = screen.querySelector('[data-auth-avatar-file-input]');
    this.avatarImageContainer = screen.querySelector('[data-auth-avatar-image-container]');
    this.avatarError = screen.querySelector('[data-avatar-error]');

    if (!this.avatarFileInput || !this.avatarImageContainer || !this.avatarError) {
      throw new Error('Avatar file input, error, or image container not found');
    }

    this.bindListeners();
  }

  bindListeners() {
    this.screen.addEventListener('click', (e) => {
      const isAvatarUploadBtn = e.target.closest('[data-auth-avatar-upload]');
      if (isAvatarUploadBtn) this.avatarFileInput.click();
    });

    this.avatarFileInput.addEventListener('change', (e) => {
      this.processAvatarFile(e.target.files?.[0]);
      this.avatarFileInput.value = '';
    });
  }

  getAvatarValidationError(file, image) {
    const MAX_FILE_SIZE_MB = 4;
    const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Only JPEG, PNG, and GIF are allowed.';
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File size exceeds the limit of ${MAX_FILE_SIZE_MB} MB.`;
    }

    if (image.width < 300 || image.height < 300) {
      return 'Minimum size is 300x300 pixels.';
    }

    return null;
  }

  setAvatarError(error) {
    if (!this.avatarError) return;
    this.avatarError.innerText = error;
    this.avatarError.toggleAttribute('hidden', !error);
  }

  async processAvatarFile(file) {
    if (!file) return;

    try {
      // Decode image
      const img = new Image();
      img.src = URL.createObjectURL(file);
      await img.decode();

      // Check for validation errors
      const error = this.getAvatarValidationError(file, img);
      this.setAvatarError(error);
      if (error) return;

      // Create a new S3 upload component
      const s3UploadEl = document.createElement('drb-s3-upload');
      const imageEl = document.createElement('img');

      s3UploadEl.file = file;
      s3UploadEl.s3Kind = 'avatar';
      s3UploadEl.hideCancel = true;
      imageEl.src = img.src;

      // Remove and cancel existing S3 upload if any
      const existingS3Upload = this.getAvatarUploadElement();
      if (existingS3Upload) existingS3Upload.cancelUpload();

      // Append the new S3 upload element
      s3UploadEl.appendChild(imageEl);
      this.avatarImageContainer.prepend(s3UploadEl);
    } catch (err) {
      this.setAvatarError('Failed to load image');
    }
  }

  getAvatarUploadElement() {
    return this.avatarImageContainer.querySelector('drb-s3-upload');
  }
}
